import { fn, numberExpr, op } from "./shared";

import type { SympyBuilder } from "./SympyBuilder";
import type { SemanticExpr } from "../types";
import type { NodeJsonMap } from "@corca/data";

/**
 * Natural log → `log(x)`.
 * A base `b` → `log(x)/log(b)`
 */
export function parseLogarithm(
  builder: SympyBuilder,
  node: NodeJsonMap["function"],
  level: number,
  defaultBase?: number,
): SemanticExpr {
  const argExprs = node.children
    .filter((n) => n.type === "inline")
    .map((n) => builder.traverseNode(n, level + 1))
    .filter(Boolean);

  const logOf = (x: SemanticExpr[]) => fn("sp.log", x);

  const symbolNode = node.children.find((n) => n.type === "symbol");
  const bottomLabelNode = symbolNode?.children.find(
    (n) => n.type === "label" && n.props.position === "right-bottom",
  );
  const baseNode = bottomLabelNode?.children.find((n) => n.type === "inline");

  if (baseNode) {
    // A subscript still being typed carries no base yet — fall through to the default.
    const baseExpr = builder.traverseNode(baseNode, level + 1);
    if (baseExpr !== null) {
      return op("Frac", [logOf(argExprs), logOf([baseExpr])]);
    }
  }

  if (defaultBase !== undefined) {
    return op("Frac", [logOf(argExprs), logOf([numberExpr(String(defaultBase))])]);
  }

  return logOf(argExprs);
}
